<template>
  <!-- State: No Assets | No Results | Error -->
  <div class="h-full flex justify-center" v-show="state">
    <slot name="state" />
  </div>

  <!-- Data: Table -->
  <DataGrid
    v-show="!state"
    :minLeftWidth="20"
    :maxLeftWidth="60"
    :leftWidth="leftWidth || 30"
    :showColumns="columns"
    class="bg-primary"
    @update:leftWidth="(value) => setPreference('assets.grid.leftWidth', value)"
  >
    <template #left>
      <Table
        :isSelectable="isSelectable"
        :showColumns="columns"
        :data="assets"
        :isLoading="isLoading"
        :sortBy="filters.sort_by"
        :orderBy="filters.order_by"
        :hasColumnPicker="false"
        :hoveredRow="hoveredRow"
        rowHeight="56"
        :type="STORE_KEY"
        @clicked:row="handleClickAsset"
        @hover:row="handleHoverRow"
        @update:sort="handleUpdateSort"
      >
        <Column
          :label="$t('admin.settings.assets.table.columns.title')"
          :isSortable="true"
          field="name"
        >
          <template #cell="{ data }">
            <div
              class="flex w-full flex-row justify-between items-center gap-2"
            >
              <div class="mr-1">
                <Indicator
                  size="xs"
                  variant="asset"
                  :imageUrl="data.thumbnailUrl"
                />
              </div>
              <div
                class="w-full leading-5 font-medium text-sm truncate flex text-primary"
              >
                <TextWithTooltip
                  :title="data.name"
                  :listeners="[leftWidth, width]"
                  :lines="2"
                />
              </div>
            </div>
          </template>
        </Column>
      </Table>
    </template>
    <template #center>
      <Table
        :showColumns="columns"
        :data="assets"
        :isLoading="isLoading"
        :sortBy="filters.sort_by"
        :orderBy="filters.order_by"
        :hasColumnPicker="true"
        :hoveredRow="hoveredRow"
        rowHeight="56"
        @clicked:row="handleClickAsset"
        @hover:row="handleHoverRow"
        @update:sort="handleUpdateSort"
        @update:column-preferences="
          (value) => setPreference('assets.grid.columns', value)
        "
      >
        <Column
          :label="$t('admin.settings.assets.table.columns.identifier')"
          :isSortable="true"
          size="100"
          field="identifier"
        >
          <template #cell="{ data }">
            <IdWithTooltip
              v-if="data.identifier"
              :label="data.identifier"
              :listeners="[leftWidth, width]"
            />
            <div v-else class="text-xs truncate text-placeholder_subtle">
              {{ $t("admin.settings.assets.table.identifier.no_value") }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.linked_to')"
          :isSortable="true"
          size="200"
          field="space.name"
        >
          <template #cell="{ data }">
            <LinkedTo
              :tooltipText="getTooltipText(data, 'space')"
              :variant="data.space ? 'space' : 'building'"
              :title="data.space ? data.space.name : data.tenant?.name"
              :text="data.space ? data.tenant?.name : ''"
              :imageUrl="data.space?.thumbnailUrl"
            />
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.qr_codes')"
          :isSortable="true"
          size="120"
          field="qr_codes"
        >
          <template #cell="{ data }">
            <div class="truncate">
              <div
                v-if="
                  Array.isArray(data.qr_codes)
                    ? data.qr_codes.length > 0
                    : data.qr_codes > 0
                "
              >
                <Badge
                  :label="
                    Array.isArray(data.qr_codes)
                      ? data.qr_codes.length
                      : data.qr_codes
                  "
                  type="color"
                  size="sm"
                  icon="leading"
                >
                  <Icon
                    name="qr-code"
                    variant="fill"
                    class="!text-utility-gray-500"
                  />
                </Badge>
              </div>
              <div v-else class="text-xs truncate text-placeholder_subtle">
                {{ $t("admin.settings.assets.table.qr_codes.no_value") }}
              </div>
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.files')"
          :isSortable="true"
          size="120"
          field="files"
        >
          <template #cell="{ data }">
            <div class="truncate">
              <div
                v-if="
                  Array.isArray(data.files)
                    ? data.files.length > 0
                    : data.files > 0
                "
              >
                <Badge
                  :label="
                    Array.isArray(data.files) ? data.files.length : data.files
                  "
                  type="color"
                  size="sm"
                  icon="leading"
                >
                  <Icon
                    name="documents"
                    variant="fill"
                    class="!text-utility-success-500"
                  />
                </Badge>
              </div>
              <div v-else class="text-xs truncate text-placeholder_subtle">
                {{ $t("admin.settings.assets.table.qr_codes.no_value") }}
              </div>
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.category')"
          :isSortable="true"
          size="120"
          field="category.name"
        >
          <template #cell="{ data }">
            <CategoryWithTooltip
              v-if="data.category"
              :category="data.category"
              :listeners="[leftWidth, width]"
            />
            <div v-else class="text-xs truncate text-placeholder_subtle">
              {{ $t("admin.settings.assets.table.category.no_value") }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.asset_group')"
          :isSortable="true"
          size="200"
          field="group.name"
        >
          <template #cell="{ data }">
            <LinkedTo
              v-if="data.group"
              mode="solid"
              :title="data.group.name"
              variant="asset_group"
            />
            <div class="text-xs truncate text-placeholder_subtle" v-else>
              {{ $t("admin.settings.assets.table.asset_group.no_value") }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.brand')"
          :isSortable="true"
          size="120"
          field="brand"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                !isEmpty(data.brand)
                  ? 'text-tertiary'
                  : 'text-placeholder_subtle'
              "
            >
              <TextWithTooltip
                :title="
                  !isEmpty(data.brand)
                    ? data.brand
                    : $t('admin.settings.assets.table.brand.no_value')
                "
                :lines="2"
              />
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.supplier')"
          :isSortable="true"
          size="120"
          field="supplier"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                !isEmpty(data.supplier)
                  ? 'text-tertiary'
                  : 'text-placeholder_subtle'
              "
            >
              <TextWithTooltip
                :title="
                  !isEmpty(data.supplier)
                    ? data.supplier
                    : $t('admin.settings.assets.table.supplier.no_value')
                "
                :lines="2"
              />
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.serial_number')"
          :isSortable="true"
          size="120"
          field="serial_number"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                !isEmpty(data.serial_number)
                  ? 'text-tertiary'
                  : 'text-placeholder_subtle'
              "
            >
              <TextWithTooltip
                :title="
                  !isEmpty(data.serial_number)
                    ? data.serial_number
                    : $t('admin.settings.assets.table.serial_number.no_value')
                "
                :lines="2"
              />
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.created_at')"
          :isSortable="true"
          size="120"
          field="created_at"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                !isEmpty(data.created_at)
                  ? 'text-tertiary'
                  : 'text-placeholder_subtle'
              "
            >
              {{
                !isEmpty(data.created_at)
                  ? formatDate(data.created_at)
                  : $t("admin.settings.assets.table.created_at.no_value")
              }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.purchased_at')"
          :isSortable="true"
          size="120"
          field="purchased_at"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                !isEmpty(data.purchased_at)
                  ? 'text-tertiary'
                  : 'text-placeholder_subtle'
              "
            >
              {{
                !isEmpty(data.purchased_at)
                  ? formatDate(data.purchased_at)
                  : $t("admin.settings.assets.table.purchased_at.no_value")
              }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.tasks_open')"
          :isSortable="true"
          size="130"
          field="tasks_open"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                data.tasks_open ? 'text-tertiary' : 'text-placeholder_subtle'
              "
            >
              {{
                data.tasks_open
                  ? data.tasks_open
                  : $t("admin.settings.assets.table.tasks_open.no_value")
              }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.tasks_scheduled')"
          :isSortable="true"
          size="130"
          field="tasks_scheduled"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                data.tasks_scheduled
                  ? 'text-tertiary'
                  : 'text-placeholder_subtle'
              "
            >
              {{
                data.tasks_scheduled
                  ? data.tasks_scheduled
                  : $t("admin.settings.assets.table.tasks_scheduled.no_value")
              }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.tasks_completed')"
          :isSortable="true"
          size="130"
          field="tasks_completed"
        >
          <template #cell="{ data }">
            <div
              class="text-xs truncate"
              :class="
                data.tasks_completed
                  ? 'text-tertiary'
                  : 'text-placeholder_subtle'
              "
            >
              {{
                data.tasks_completed
                  ? data.tasks_completed
                  : $t("admin.settings.assets.table.tasks_completed.no_value")
              }}
            </div>
          </template>
        </Column>

        <Column
          :label="$t('admin.settings.assets.table.columns.tags')"
          size="250"
          field="tags"
        >
          <template #cell="{ data }">
            <TagsWithTooltip :tags="data.tags" />
          </template>
        </Column>
      </Table>
    </template>
    <template #footer>
      <Pagination
        :total="totalAssets"
        :perPage="perPage"
        :page="page"
        :perPageOptions="[25, 50, 100]"
        @update:perPage="handleUpdatePerPage($event)"
        @update:page="handleUpdatePage($event)"
      >
        <template #actions>
          <AssetExportModal :filters="filters" />
        </template>
      </Pagination>
    </template>
  </DataGrid>

  <template v-if="$slots['bulk-actions']">
    <slot name="bulk-actions" :selectedAssets="selectedAssets" />
  </template>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue";
import axios from "axios";
import route from "ziggy-js";
import { isEmpty } from "lodash";
import { storeToRefs } from "pinia";

import { TextWithTooltip } from "@/SharedComponents/TextWithTooltip";
import { CategoryWithTooltip } from "@/SharedComponents/CategoryWithTooltip";
import { IdWithTooltip } from "@/SharedComponents/IdWithTooltip";
import { Indicator } from "@/SharedComponents/Indicator";
import { Table, Column, DataGrid } from "@/SharedComponents/DataGrid";
import { Pagination } from "@/SharedComponents/Pagination";
import { AssetExportModal } from "@/SharedComponents/Modals";
import { LinkedTo } from "@/SharedComponents/LinkedTo";
import { Badge } from "@/SharedComponents/Badges";
import { Icon } from "@/SharedComponents/Icon";

// Importing composables
import { useDate } from "@/Composables/useDate";
import { useFilters } from "@/Composables/useFilters";
import { usePreferences } from "@/Composables/usePreferences";
import { useSearch } from "@/Composables/useSearch";
import { useSorting } from "@/Composables/useSorting";
import { useSelectedRows } from "@/Composables/useSelectedRows";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { TagsWithTooltip } from "@/SharedComponents/TagsWithTooltip";

const STORE_KEY = "assets";

// Date utility
const { formatDate } = useDate();

// Preferences
const { setPreference, preferences } = usePreferences();

// Sorting
const sortingStore = useSorting(STORE_KEY);
const { handleSortingChange } = sortingStore;
const { sortingValues } = storeToRefs(sortingStore);

// Filters
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);
const filterStore = useFilters(STORE_KEY);
const { filterValues } = storeToRefs(filterStore);

// Search
const searchStore = useSearch(STORE_KEY);
const { searchValues } = storeToRefs(searchStore);

// Selection
const selectedRowsStore = useSelectedRows(STORE_KEY);
const { selection } = storeToRefs(selectedRowsStore);
const { handleClearSelection } = selectedRowsStore;

// Breakpoints
const { width } = useBreakpoints();

// Emits
const emit = defineEmits([
  "show:detail",
  "update:count",
  "update:fetchingStatus",
]);

// Define Props
const props = defineProps({
  count: { type: Number, default: 0 },
  fetchingStatus: { type: String, required: true },
  isSelectable: { type: Boolean, required: false },
});

// Emitter
const emitter = inject("emitter");

// Refs
const page = ref(1);
const perPage = ref(25);
const assets = ref([]);
const totalAssets = ref(0);
const isLoading = ref(false);
const hoveredRow = ref(null);

// Computed Props
const selectedAssets = computed(() =>
  assets.value.filter((asset) => selection.value.includes(asset.id))
);

const state = computed(() => {
  if (props.fetchingStatus === "loading") {
    return false;
  }

  return props.count === 0 || props.fetchingStatus === "error";
});

const columns = computed(() => {
  return preferences.value["assets.grid.columns"]
    ? preferences.value["assets.grid.columns"]
    : [
        "identifier",
        "space.name",
        "qr_codes",
        "files",
        "category.name",
        "group.name",
        "brand",
        "supplier",
        "serial_number",
        "created_at",
        "purchased_at",
        "tasks_open",
        "tasks_scheduled",
        "tasks_completed",
        "tags",
      ];
});

const leftWidth = computed(() => {
  return preferences.value["assets.grid.leftWidth"];
});

const filters = computed(() => ({
  ...sortingValues.value,
  ...searchValues.value,
  ...filterValues.value,
  tenant: globalFilterValues.value.tenant,
  tenant_only: filterValues.value.tenant?.length > 0,
}));

// Functions
const handleClickAsset = (asset) => {
  emit("show:detail", asset);
};

const handleHoverRow = (row) => {
  hoveredRow.value = row;
};

const handleUpdatePerPage = (value) => {
  perPage.value = value;
  handleUpdatePage(1);
};

const handleUpdatePage = (value) => {
  page.value = value;
  handleClearSelection();
  getAssets();
};

const handleUpdateSort = (field, order) => {
  handleSortingChange({ id: field, desc: order === "desc" });
};

// Get Data
async function getAssets() {
  isLoading.value = true;
  assets.value = [];

  const parameters = {
    page: page.value,
    ...filters.value,
    per_page: perPage.value,
  };

  emit("update:fetchingStatus", "loading");

  const url = route("api.assets.search");
  await axios
    .post(url, {
      ...parameters,
    })
    .then((response) => {
      assets.value = response.data.data;
      totalAssets.value = response.data.meta.total;
      emit("update:count", response.data.meta.total);
      emit("update:fetchingStatus", "loaded");
    })
    .catch((err) => {
      console.log("error occurred", err);
      emit("update:count", 0);
      emit("update:fetchingStatus", "error");
    })
    .finally(() => {
      isLoading.value = false;
    });
}

const getTooltipText = (data, type = null) => {
  let tooltipText = data.tenant?.name + " > ";
  if (type === "space" && data.parents?.length > 0) {
    return tooltipText + data.parents.map((item) => item.name).join(" > ");
  }

  return "";
};

// Emitters
emitter.on("update-assets-data", (e) => {
  assets.value = assets.value.map((asset) => {
    const updatedAsset = e.data.find((a) => a.id === asset.id);
    return updatedAsset ? updatedAsset : asset;
  });
});

emitter.on("remove-assets-data", (e) => {
  assets.value = assets.value.filter((asset) => !e.data.includes(asset.id));
  totalAssets.value -= e.data.length;
});

emitter.on("reload-assets", () => {
  page.value = 1;
  getAssets();
});

// Watchers
watch(
  filters,
  () => {
    page.value = 1;
    handleClearSelection();
    getAssets();
  },
  { deep: true }
);

// Lifecycle hooks
onMounted(() => {
  getAssets();
});

onUnmounted(() => {
  handleClearSelection();
});
</script>
